import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { genres } from "@/mocks/films";

const navItems = [
  { label: "首页", to: "/" },
  { label: "影视分类", to: "/category" },
  { label: "关于我们", to: "/about" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [query, setQuery] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const keyword = query.trim();
    navigate(keyword ? `/search?query=${encodeURIComponent(keyword)}` : "/search");
    setMenuOpen(false);
  };

  const solid = !isHome || scrolled;
  const linkBase = "relative text-sm transition-colors duration-200";
  const linkColor = solid
    ? "text-foreground-700 hover:text-primary-600"
    : "text-background-50/85 hover:text-background-50";

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        solid
          ? "border-b border-background-200/80 bg-background-50/95 backdrop-blur-md"
          : "border-b border-transparent bg-gradient-to-b from-foreground-950/55 to-transparent"
      }`}
    >
      <div className="flex h-16 w-full items-center justify-between gap-6 px-4 md:h-[72px] md:px-8">
        <Link to="/" className="flex shrink-0 items-center gap-2.5 cursor-pointer">
          <span
            className={`flex h-9 w-9 items-center justify-center rounded-md text-sm font-bold ${
              solid ? "bg-primary-500 text-background-50" : "bg-background-50 text-primary-600"
            }`}
          >
            色
          </span>
          <span className="leading-tight">
            <span
              className={`block font-heading text-[17px] font-bold tracking-wide ${
                solid ? "text-foreground-950" : "text-background-50"
              }`}
            >
              色即是空
            </span>
            <span
              className={`block text-[10px] uppercase tracking-[0.28em] ${
                solid ? "text-foreground-400" : "text-background-100/70"
              }`}
            >
              Cinema Archive
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          {navItems.map((item) => {
            const active =
              item.to === "/"
                ? location.pathname === "/"
                : location.pathname.startsWith(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`${linkBase} ${linkColor} ${active ? "font-semibold" : ""}`}
              >
                {item.label}
                {active ? (
                  <span className="absolute -bottom-1.5 left-0 h-[2px] w-full rounded-full bg-accent-500" />
                ) : null}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          <form
            onSubmit={handleSearch}
            className={`hidden items-center gap-2 rounded-full border px-3 py-1.5 lg:flex ${
              solid
                ? "border-background-300/80 bg-background-100"
                : "border-background-50/30 bg-background-50/10"
            }`}
          >
            <span
              className={`flex h-5 w-5 items-center justify-center text-sm ${
                solid ? "text-foreground-500" : "text-background-50/80"
              }`}
            >
              <i className="ri-search-line" />
            </span>
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="搜索片名或演员"
              aria-label="搜索影视作品"
              className={`w-36 bg-transparent text-sm outline-none xl:w-44 ${
                solid
                  ? "text-foreground-900 placeholder:text-foreground-400"
                  : "text-background-50 placeholder:text-background-50/60"
              }`}
            />
          </form>

          <Link
            to="/category"
            className="hidden shrink-0 items-center gap-1.5 rounded-md bg-accent-500 px-4 py-2 text-sm font-medium whitespace-nowrap text-foreground-950 transition-colors hover:bg-accent-400 md:inline-flex"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-compass-3-line text-sm" />
            </span>
            开始探索
          </Link>

          <button
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            aria-label="打开菜单"
            className={`flex h-10 w-10 items-center justify-center rounded-md md:hidden ${
              solid
                ? "bg-background-100 text-foreground-800"
                : "bg-background-50/15 text-background-50"
            }`}
          >
            <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
          </button>
        </div>
      </div>

      {menuOpen ? (
        <div className="fixed inset-x-0 top-16 bottom-0 z-50 overflow-y-auto border-t border-background-200 bg-background-50 px-4 pb-10 pt-5 md:hidden">
          <form
            onSubmit={handleSearch}
            className="mb-5 flex items-center gap-2 rounded-full border border-background-300 bg-background-100 px-4 py-2.5"
          >
            <span className="flex h-5 w-5 items-center justify-center text-foreground-500">
              <i className="ri-search-line" />
            </span>
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="搜索片名或演员"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
            <button type="submit" className="text-sm font-medium text-primary-600 whitespace-nowrap">
              搜索
            </button>
          </form>
          <div className="flex flex-col">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="flex items-center justify-between border-b border-background-200 py-3.5 text-[15px] text-foreground-900"
              >
                {item.label}
                <span className="flex h-5 w-5 items-center justify-center text-foreground-400">
                  <i className="ri-arrow-right-s-line" />
                </span>
              </Link>
            ))}
          </div>
          <p className="mb-3 mt-6 text-xs uppercase tracking-[0.2em] text-foreground-400">按类型浏览</p>
          <div className="flex flex-wrap gap-2">
            {genres.slice(0, 8).map((genre) => (
              <Link
                key={genre}
                to={`/category?genre=${encodeURIComponent(genre)}`}
                className="rounded-full bg-secondary-100 px-3 py-1.5 text-xs text-secondary-900"
              >
                {genre}
              </Link>
            ))}
          </div>
        </div>
      ) : null}
    </header>
  );
};

export default Navbar;